import axios from "axios";
import type { SpecsCatalog } from "../containers/SpecsEditor/specsModel/catalog";
import type { SpecsModelSnapshot } from "../containers/SpecsEditor/specsModel/types";

const SPECS_SERVICE_URL = (
  process.env.REACT_APP_SPECS_SERVICE_URL || ""
).replace(/\/$/, "");

export type SpecType = "family" | "product" | "fabric" | "decoration";
export type SpecsRevisionServiceOptions = {
  specsServiceUrl?: string;
};

export interface SpecsHistoryRevision {
  revisionId: string;
  createdAt: string;
  createdBy: string;
  sourceRevisionId: string | null;
  changeNote: string;
  isCurrentPublished: boolean;
  isCurrentDraft: boolean;
}

export interface SpecsHistoryResponse {
  revisions: SpecsHistoryRevision[];
  nextCursor: string | null;
  currentPublishedRevisionId: string | null;
  currentDraftRevisionId: string | null;
}

export interface SpecsRevisionRecord {
  specKey: string;
  revisionId: string;
  specType: SpecType;
  json: Record<string, any>;
  createdAt: string;
  createdBy: string;
  sourceRevisionId: string | null;
  changeNote: string;
}

export interface SpecsImageUploadResponse {
  uploadUrl: string;
  assetUrl: string;
  objectKey: string;
  fields: Record<string, string>;
}

export interface SpecsRuntimeResponse {
  revisionId: string | null;
  specs: Record<string, any>;
}

export interface PromoteRevisionResponse {
  revisionId: string;
  nextEnv?: string;
}

export interface NextEnvSpecsCatalogResponse {
  nextEnv: string;
  specs: SpecsCatalog;
}

export interface SpecsValidationIssue {
  validator?: string;
  message: string;
  details?: string;
  stack?: string;
  path?: string | (string | number)[];
  severity?: "error" | "warning";
  fields?: string[];
}

export interface SpecsValidationResult {
  valid: boolean;
  issues: SpecsValidationIssue[];
  imageValidationSkipped?: boolean;
}

export interface DraftProductSnapshotsResponse {
  snapshots: SpecsModelSnapshot[];
}

export interface DraftSpecsSnapshotsResponse {
  snapshots: Record<string, unknown>[];
}

export interface DraftNonProductSnapshotsResponse {
  snapshots: {
    families: Record<string, unknown>[];
    fabrics: Record<string, unknown>[];
    decorations: Record<string, unknown>[];
  };
}

function getSpecsServiceUrl(options: SpecsRevisionServiceOptions = {}) {
  if (options.specsServiceUrl) {
    return options.specsServiceUrl.replace(/\/$/, "");
  }

  if (!SPECS_SERVICE_URL) {
    throw new Error("Missing REACT_APP_SPECS_SERVICE_URL");
  }

  return SPECS_SERVICE_URL;
}

function buildSpecPath(specType: SpecType, specName: string) {
  return `${encodeURIComponent(specType)}/${encodeURIComponent(specName)}`;
}

function isValidationResult(value: unknown): value is SpecsValidationResult {
  return (
    !!value &&
    typeof value === "object" &&
    typeof (value as SpecsValidationResult).valid === "boolean" &&
    Array.isArray((value as SpecsValidationResult).issues)
  );
}

type SpecsServiceFieldIssueResponse = {
  issues: unknown[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function isFieldIssuesResponse(
  data: unknown
): data is SpecsServiceFieldIssueResponse {
  return (
    isRecord(data) &&
    Array.isArray(data.issues) &&
    data.issues.some(
      (issue) => isRecord(issue) && typeof issue.message === "string"
    )
  );
}

function normalizeSpecsValidationIssue(issue: unknown): SpecsValidationIssue {
  if (!isRecord(issue) || typeof issue.message !== "string") {
    return {
      message: "Specs validation failed",
      severity: "error",
    };
  }

  const path =
    typeof issue.path === "string"
      ? issue.path
      : Array.isArray(issue.path)
        ? issue.path.filter(
            (segment): segment is string | number =>
              typeof segment === "string" || typeof segment === "number"
          )
        : undefined;
  const severity = issue.severity === "warning" ? "warning" : "error";
  const fields = Array.isArray(issue.fields)
    ? issue.fields.filter((field): field is string => typeof field === "string")
    : undefined;

  return {
    message: issue.message,
    validator:
      typeof issue.validator === "string" ? issue.validator : undefined,
    details: typeof issue.details === "string" ? issue.details : undefined,
    stack: typeof issue.stack === "string" ? issue.stack : undefined,
    path,
    severity,
    fields,
  };
}

export function extractSpecsValidationResult(
  error: unknown
): SpecsValidationResult | null {
  if (!axios.isAxiosError(error)) {
    return null;
  }

  const status = error.response?.status;
  const data = error.response?.data;

  // specs-service returns 400 with { issues: FieldIssue[] }
  if (status === 400 && isFieldIssuesResponse(data)) {
    return {
      valid: false,
      issues: data.issues.map(normalizeSpecsValidationIssue),
    };
  }

  // Legacy 422 format from older validation endpoints
  if (status === 422) {
    if (isValidationResult(data)) {
      return data;
    }
    if (isValidationResult(data?.validation)) {
      return data.validation;
    }
    if (isValidationResult(data?.validationResult)) {
      return data.validationResult;
    }
  }

  if (status === 400 || status === 422) {
    const message =
      typeof data?.error === "string" ? data.error : "Specs validation failed";
    return {
      valid: false,
      issues: [{ message }],
    };
  }

  return null;
}

class SpecsRevisionService {
  static async saveSpecRevision(
    data: {
      specType: SpecType;
      specName: string;
      snapshot: Record<string, any>;
      sourceRevisionId?: string | null;
      createdBy?: string;
      changeNote?: string;
      parentKey?: string;
    },
    options: SpecsRevisionServiceOptions = {}
  ) {
    return axios.post(`${getSpecsServiceUrl(options)}/specs/revisions`, data, {
      withCredentials: true,
    });
  }

  static async getPublishedSpecsCatalog() {
    return axios.get<SpecsCatalog>(`${getSpecsServiceUrl()}/specs.json`, {
      withCredentials: true,
    });
  }

  static async getNextEnvPublishedSpecsCatalog() {
    return axios.get<NextEnvSpecsCatalogResponse>(
      `${getSpecsServiceUrl()}/specs/next-env/specs.json`,
      {
        withCredentials: true,
      }
    );
  }

  static async getDraftProductSnapshots() {
    return axios.get<DraftProductSnapshotsResponse>(
      `${getSpecsServiceUrl()}/specs/product-drafts`,
      {
        withCredentials: true,
      }
    );
  }

  static async getDraftFabricSnapshots() {
    return axios.get<DraftSpecsSnapshotsResponse>(
      `${getSpecsServiceUrl()}/specs/fabric-drafts`,
      {
        withCredentials: true,
      }
    );
  }

  static async getDraftNonProductSnapshots() {
    return axios.get<DraftNonProductSnapshotsResponse>(
      `${getSpecsServiceUrl()}/specs/non-product-drafts`,
      {
        withCredentials: true,
      }
    );
  }

  static async listSpecRevisions(
    specType: SpecType,
    specName: string,
    options: { limit?: number; cursor?: string | null } = {}
  ) {
    return axios.get<SpecsHistoryResponse>(
      `${getSpecsServiceUrl()}/specs/${buildSpecPath(specType, specName)}/revisions`,
      {
        params: {
          limit: options.limit,
          cursor: options.cursor || undefined,
        },
        withCredentials: true,
      }
    );
  }

  static async getSpecRevisionRuntimeSpecs(
    specType: SpecType,
    specName: string,
    revisionId: string
  ) {
    return axios.get<SpecsRuntimeResponse>(
      `${getSpecsServiceUrl()}/specs/${buildSpecPath(specType, specName)}/revisions/${encodeURIComponent(revisionId)}/runtime`,
      {
        withCredentials: true,
      }
    );
  }

  static async getSpecRevision(
    specType: SpecType,
    specName: string,
    revisionId: string
  ) {
    return axios.get<SpecsRevisionRecord>(
      `${getSpecsServiceUrl()}/specs/${buildSpecPath(specType, specName)}/revisions/${encodeURIComponent(revisionId)}`,
      {
        withCredentials: true,
      }
    );
  }

  static async validateSpecRevision(
    specType: SpecType,
    specName: string,
    revisionId: string
  ) {
    return axios.post<SpecsValidationResult>(
      `${getSpecsServiceUrl()}/specs/${buildSpecPath(specType, specName)}/revisions/${encodeURIComponent(revisionId)}/validate`,
      {},
      {
        withCredentials: true,
      }
    );
  }

  static async publishSpecRevision(
    specType: SpecType,
    specName: string,
    revisionId: string
  ) {
    return axios.post(
      `${getSpecsServiceUrl()}/specs/${buildSpecPath(specType, specName)}/revisions/${encodeURIComponent(revisionId)}/publish`,
      {},
      {
        withCredentials: true,
      }
    );
  }

  static async promoteRevisionToNext(
    specType: SpecType,
    specName: string,
    revisionId: string
  ) {
    return axios.post<PromoteRevisionResponse>(
      `${getSpecsServiceUrl()}/specs/${buildSpecPath(specType, specName)}/revisions/${encodeURIComponent(revisionId)}/promote-to-next`,
      {},
      {
        withCredentials: true,
      }
    );
  }

  static async deleteSpec(specType: SpecType, specName: string) {
    return axios.post(
      `${getSpecsServiceUrl()}/specs/${buildSpecPath(specType, specName)}/delete`,
      {},
      {
        withCredentials: true,
      }
    );
  }

  static async createImageUpload(
    data: {
      fileName: string;
      contentType: string;
      specType?: SpecType;
      specName?: string;
      fieldPath?: string;
    },
    options: SpecsRevisionServiceOptions = {}
  ) {
    return axios.post<SpecsImageUploadResponse>(
      `${getSpecsServiceUrl(options)}/uploads/images/presign`,
      data,
      {
        withCredentials: true,
      }
    );
  }
}

export default SpecsRevisionService;
